/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css } from 'lit-element';

/**
 * Button CSS
 */
export const styles = css`
  :host {
    display: inline-block;
    position: relative;
    width: auto;
    cursor: pointer;
  }

  .button {
    display: inline-flex;
    align-items: stretch;
    justify-content: center;
    width: 100%;
    border-style: solid;
    border-width: 1px;
    font-style: normal;
    font-weight: 400;
    font-size: var(--nile-font-size-base);
    line-height: var(--nile-font-size-base);
    text-align: center;
    letter-spacing: 0.2px;
    text-decoration: none;
    user-select: none;
    white-space: nowrap;
    vertical-align: middle;
    padding: var(--nile-spacing-one-half-x);
    transition: var(--nile-transition-duration-default) background-color,
      var(--nile-transition-duration-default) color,
      var(--nile-transition-duration-default) border,
      var(--nile-transition-duration-default) box-shadow;
    cursor: inherit;
  }

  .button::-moz-focus-inner {
    border: 0;
  }

  .button:focus {
    outline: none;
  }

  .button:focus-visible {
    outline: 5px auto -webkit-focus-ring-color;
    outline-offset: 2px;
  }

  .button--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .button--disabled * {
    pointer-events: none;
  }

  .button__prefix,
  .button__suffix {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    pointer-events: none;
  }
  .button__label {
    display: inline-block;
  }

  .button__label::slotted(nile-icon) {
    vertical-align: -2px;
  }

  .button--standard.button--secondary {
    background-color: var(--nile-colors-neutral-400);
    border-color: var(--nile-colors-neutral-400);
    color: var(--nile-colors-neutral-700);
  }

  .button--standard.button--secondary:hover:not(.button--disabled) {
    background-color: var(--nile-colors-neutral-500);
    border-color: var(--nile-colors-neutral-500);
    box-shadow: 0px 4px 8px var(--nile-colors-dark-200);
    color: var(--nile-colors-neutral-700);
  }

  .button--standard.button--secondary:active:not(.button--disabled) {
    background-color: var(--nile-colors-neutral-500);
    border-color: var(--nile-colors-neutral-700);
    color: var(--nile-colors-neutral-700);
  }

  .button--standard.button--secondary.button--disabled {
    background-color: var(--nile-colors-neutral-400);
    border-color: var(--nile-colors-neutral-400);
    color: var(--nile-colors-dark-500);
    cursor: not-allowed;
  }

  .button--standard.button--secondary.button--disabled:hover,
  .button--standard.button--secondary.button--disabled:active {
    background-color: var(--nile-colors-neutral-400);
    border-color: var(--nile-colors-neutral-400);
    color: var(--nile-colors-dark-500);
    box-shadow: none;
  }

  /* Primary */
  .button--standard.button--primary {
    background-color: var(--nile-colors-primary-600);
    border-color: var(--nile-colors-primary-600);
    color: var(--nile-colors-white-base);
  }

  .button--standard.button--primary:hover:not(.button--disabled) {
    background-color: var(--nile-colors-primary-700);
    border-color: var(--nile-colors-primary-700);
    box-shadow: 0px 4px 8px var(--nile-colors-dark-200);
    color: var(--nile-colors-white-base);
  }

  .button--standard.button--primary:active:not(.button--disabled) {
    background-color: var(--nile-colors-primary-700);
    border-color: var(--nile-colors-primary-900);
    color: var(--nile-colors-white-base);
  }

  .button--standard.button--primary.button--disabled {
    background-color: var(--nile-colors-primary-600);
    border-color: var(--nile-colors-primary-600);
    color: var(--nile-colors-neutral-700);
    cursor: not-allowed;
  }

  .button--standard.button--primary.button--disabled:hover,
  .button--standard.button--primary.button--disabled:active {
    background-color: var(--nile-colors-primary-600);
    border-color: var(--nile-colors-primary-600);
    color: var(--nile-colors-neutral-700);
    box-shadow: none;
  }

  /* Tertiary */
  .button--standard.button--tertiary {
    background-color: var(--nile-colors-white-base);
    border-color: var(--nile-colors-neutral-500);
    color: var(--nile-colors-neutral-700);
    box-shadow: 0px 2px 4px var(--nile-colors-dark-200);
  }

  .button--standard.button--tertiary:hover:not(.button--disabled) {
    background-color: var(--nile-colors-neutral-100);
    border-color: var(--nile-colors-neutral-500);
    box-shadow: 0px 4px 8px var(--nile-colors-dark-200);
    color: var(--nile-colors-neutral-700);
  }

  .button--standard.button--tertiary:active:not(.button--disabled) {
    background-color: var(--nile-colors-neutral-400);
    border-color: var(--nile-colors-neutral-700);
    color: var(--nile-colors-neutral-700);
  }
  .button--standard.button--tertiary.button--disabled {
    background-color: var(--nile-colors-white-base);
    border-color: var(--nile-colors-neutral-400);
    color: var(--nile-colors-neutral-700);
    cursor: not-allowed;
    box-shadow: none;
  }

  .button--standard.button--tertiary.button--disabled:hover,
  .button--standard.button--tertiary.button--disabled:active {
    background-color: var(--nile-colors-white-base);
    border-color: var(--nile-colors-neutral-400);
    color: var(--nile-colors-neutral-700);
    box-shadow: none;
  }

  /* caution */
  .button--standard.button--caution {
    background-color: var(--nile-colors-red-500);
    border-color: var(--nile-colors-red-500);
    color: var(--nile-colors-white-base);
  }
  .button--standard.button--caution:hover:not(.button--disabled) {
    background-color: var(--nile-colors-red-700);
    border-color: var(--nile-colors-red-700);
    color: var(--nile-colors-white-base);
  }

  .button--standard.button--caution:active:not(.button--disabled) {
    background-color: var(--nile-colors-red-700);
    border-color: var(--nile-colors-red-500);
    color: var(--nile-colors-white-base);
  }

  .button--standard.button--tertiary.button--disabled {
    background-color: var(--nile-colors-neutral-100);
    border-color: var(--nile-colors-neutral-100);
    color: var(--nile-colors-neutral-400);
    cursor: not-allowed;
    box-shadow: none;
  }

  .button--standard.button--tertiary.button--disabled:hover,
  .button--standard.button--tertiary.button--disabled:active {
    background-color: var(--nile-colors-neutral-100);
    border-color: var(--nile-colors-neutral-100);
    color: var(--nile-colors-neutral-400);
    box-shadow: none;
  }

  /*
 * Size modifiers
 */

  .button--small {
    height: auto;
    min-height: 24px;
    font-size: 0.875rem;
    line-height: calc(24px - var(--nile-spacing-one-half-x) * 2);
    border-radius: var(--nile-radius-base-standard);
  }

  .button--medium {
    height: auto;
    min-height: 38px;
    font-size: 0.875rem;
    line-height: calc(38px - var(--nile-spacing-one-half-x) * 2);
    font-size: var(--nile-font-size-base);
    border-radius: var(--nile-radius-base-standard);
  }

  .button--large {
    height: auto;
    min-height: 40px;
    font-size: 1rem;
    line-height: calc(40px - var(--nile-spacing-one-half-x) * 2);
    border-radius: 6px;
  }

  /*
 * Pill modifier
 */

  .button--pill.button--small {
    border-radius: 24px;
  }

  .button--pill.button--medium {
    border-radius: 32px;
  }

  .button--pill.button--large {
    border-radius: 40px;
  }

  /*
 * Circle modifier
 */

  .button--circle {
    padding-left: 0;
    padding-right: 0;
  }

  .button--circle.button--small {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  .button--circle.button--medium {
    width: 38px;
    height: 38px;
    border-radius: 50%;
  }

  .button--circle.button--large {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }

  .button--circle .button__prefix,
  .button--circle .button__suffix,
  .button--circle .button__caret {
    display: none;
  }

  /* Caret modifier */

  .button--caret .button__suffix {
    display: none;
  }

  .button--caret .button__caret {
    height: auto;
  }

  /*
 * Loading modifier
 */

  .button--loading {
    position: relative;
    cursor: wait;
  }

  .button--loading .button__prefix,
  .button--loading .button__label,
  .button--loading .button__suffix,
  .button--loading .button__caret {
    visibility: hidden;
  }

  .button--loading nile-spinner {
    --indicator-color: currentColor;
    position: absolute;
    font-size: 1em;
    height: 1em;
    width: 1em;
    top: calc(50% - 0.5em);
    left: calc(50% - 0.5em);
  }
  /*
 * Badges
 */

  .button ::slotted(nile-badge) {
    position: absolute;
    top: 0;
    right: 0;
    translate: 50% -50%;
    pointer-events: none;
  }

  .button--rtl ::slotted(nile-badge) {
    right: auto;
    left: 0;
    translate: -50% -50%;
  }

  /*
 * Button spacing
 */

  .button--has-label.button--small .button__label {
    padding: 0 var(--nile-spacing-one-half-x);
  }

  .button--has-label.button--medium .button__label {
    padding: 0 var(--nile-spacing-2-x);
  }

  .button--has-label.button--large .button__label {
    padding: 0 var(--nile-spacing-3-x);
  }

  .button--has-prefix.button--small {
    padding-inline-start: var(--nile-spacing-one-half-x);
  }

  .button--has-prefix.button--small .button__label {
    padding-inline-start: var(--nile-spacing-one-half-x);
  }

  .button--has-prefix.button--medium {
    padding-inline-start: var(--nile-spacing-2-x);
  }

  .button--has-prefix.button--medium .button__label {
    padding-inline-start: var(--nile-spacing-2-x);
  }

  .button--has-prefix.button--large {
    padding-inline-start: var(--nile-spacing-2-x);
  }

  .button--has-prefix.button--large .button__label {
    padding-inline-start: var(--nile-spacing-2-x);
  }

  .button--has-suffix.button--small,
  .button--caret.button--small {
    padding-inline-end: var(--nile-spacing-one-half-x);
  }

  .button--has-suffix.button--small .button__label,
  .button--caret.button--small .button__label {
    padding-inline-end: var(--nile-spacing-one-half-x);
  }

  .button--has-suffix.button--medium,
  .button--caret.button--medium {
    padding-inline-end: var(--nile-spacing-2-x);
  }

  .button--has-suffix.button--medium .button__label,
  .button--caret.button--medium .button__label {
    padding-inline-end: var(--nile-spacing-2-x);
  }

  .button--has-suffix.button--large,
  .button--caret.button--large {
    padding-inline-end: var(--nile-spacing-2-x);
  }

  .button--has-suffix.button--large .button__label,
  .button--caret.button--large .button__label {
    padding-inline-end: var(--nile-spacing-2-x);
  }
`;
